import {
  CodeRegister,
  constants,
  dataServices,
  OrderData as CoreOrderData,
  Promise as BluebirdPromise,
  Realty as CoreRealty,
  services
} from '@ambita/ambita-components-core';
import {
  API_SEARCH_CADASTRES,
  PRODUCT_NEIGHBOR_LIST,
  PRODUCT_NEIGHBOR_LIST_CADASTRE_INPUT,
  PRODUCT_NEIGHBOR_LIST_NEW,
  PRODUCT_NEIGHBOR_LIST_PROD
} from '../constants/common';
import { Order } from '../typings/Order';
import { ApiOwnership, MainCadastreOwner, NeighborCadastreOwner } from '../typings/Owner';
import { NeighborData, NeighborListReport } from '../typings/NeighborList';
import { fetchOrder, getProductOrderData } from './common';
import { getConfig } from './config';
import { composeOrderDataForCadastreInput } from './neighborListOrderData';
import * as realty from './realty';

const { util } = services;

const COUNTY_ID_LENGTH = 2;
const MUNICIPALITY_ID_LENGTH = 4;
const PERSON_DEAD_CODE = constants.realty.person.dsfStates.dead.code;

const doesCadastreHaveOwners = (cadastre: realty.Cadastre): boolean => {
  return cadastre.owners && cadastre.owners.length > 0;
};

const doesCadastreHaveUnregisteredOwnershipsOwningThisCadastre = (cadastre: realty.Cadastre): boolean => {
  return cadastre.unregisteredOwnershipsOwningThisCadastre
    && cadastre.unregisteredOwnershipsOwningThisCadastre.length > 0;
};

const doesCadastreHaveOwnershipsOwningThisCadastre = (cadastre: realty.Cadastre): boolean => {
  return cadastre.ownershipsOwningThisCadastre && cadastre.ownershipsOwningThisCadastre.length > 0;
};

const isHistoricalCadastre = (cadastre: realty.Cadastre): boolean => {
  return cadastre.isHistoricalRegisteredLand || cadastre.isHistoricalTechnicalRegisteredLand;
};

const collectOwnerDetails = (owner: CoreRealty.ApiPerson): BluebirdPromise<MainCadastreOwner> => {
  if (owner && owner.organizationNumber) {
    const organizationDataOverride = getConfig().organizationDataOverrides[String(owner.organizationNumber)];

    if (organizationDataOverride) {
      return BluebirdPromise.resolve(
        services.util.assign({}, owner, {
          name: organizationDataOverride.name,
          address: organizationDataOverride.address
        })
      );
    }

    return realty.fetchPersons(owner.organizationNumber.toString()).then((organizations) => {
      return services.util.assign({}, owner, {
        address: realty.getOrganizationAddress(organizations[0])
      });
    });
  } else if (owner && owner.birthday && owner.links && owner.links.residence) {
    return realty.fetchResourcesByLink(owner.links.residence.href).then((residenceAddresses) => {
      return services.util.assign({}, owner, {
        address: realty.mapAddress(residenceAddresses[0])
      });
    });
  } else if (owner && owner.birthday && owner.links && !owner.links.residence && owner.foreignAddress) {
    return BluebirdPromise.resolve(services.util.assign({}, owner, {
      address: {
        title: owner.foreignAddress,
        postcode: '',
        city: ''
      }
    }));
  } else if (owner && owner.dsfState && owner.dsfState.code === PERSON_DEAD_CODE) {
    const DEAD_OWNER = 'Hjemmelshaver død';

    return BluebirdPromise.resolve(services.util.assign({}, owner, {
      address: {
        title: DEAD_OWNER,
        postcode: '',
        city: ''
      }
    }));
  } else if (owner && !owner.location) {
    const LOCATION_MISSING = 'Eiers addresse mangler';

    return BluebirdPromise.resolve(services.util.assign({}, owner, {
      address: {
        title: LOCATION_MISSING
      }
    }));
  }

  return BluebirdPromise.resolve(services.util.assign({}, owner, {
    address: realty.createEmptyAddress()
  }));
};

const getOwnerWithFractionData = (ownership: realty.OwnershipData): BluebirdPromise<MainCadastreOwner> => {
  const { cadastreLevel, fraction, links: { owningPerson } } = ownership;

  if (owningPerson) {
    return realty.fetchResourcesByLink(owningPerson.href)
      .then((owner: CoreRealty.ApiPerson): BluebirdPromise<MainCadastreOwner> => {
        if (owner[0]) {
          owner[0].fraction = fraction;
          owner[0].role = cadastreLevel.text;
        }

        return collectOwnerDetails(owner[0]);
      });
  } else {
    return BluebirdPromise.resolve({
      name: '',
      address: {
        city: '',
        postcode: '',
        title: ''
      },
      fraction
    } as MainCadastreOwner);
  }
};

const getUnregisteredOwner = (
  unregisteredOwnership: realty.UnregisteredOwnershipData
): BluebirdPromise<NeighborCadastreOwner> => {
  const {
    cadastreLevel,
    fraction = {
      numerator: 0,
      denominator: 0
    },
    links: {
      owningPerson
    }
  } = unregisteredOwnership;
  const NOT_REGISTERED = 'Ikke tinglyst';

  if (owningPerson) {
    return realty
      .fetchResourcesByLink(owningPerson.href)
      .then((owner: CoreRealty.ApiPerson): BluebirdPromise<NeighborCadastreOwner> => {
        return collectOwnerDetails(owner[0]).then(details => ({
          ...details,
          additionalInfo: NOT_REGISTERED,
          fraction,
          role: cadastreLevel.text
        }));
      });
  }

  return BluebirdPromise.resolve({
    name: '',
    address: {
      city: '',
      postcode: '',
      title: ''
    },
    additionalInfo: NOT_REGISTERED,
    fraction,
    role: cadastreLevel.text
  } as NeighborCadastreOwner);
};

const getOwnersFromOwnerships = (cadastre: realty.Cadastre): BluebirdPromise<NeighborCadastreOwner[]> => {
  if (cadastre.ownershipsOwningThisCadastre && cadastre.ownershipsOwningThisCadastre.length > 0) {
    return BluebirdPromise.all(cadastre.ownershipsOwningThisCadastre.map(getOwnerWithFractionData));
  } else if (doesCadastreHaveUnregisteredOwnershipsOwningThisCadastre(cadastre)) {
    return BluebirdPromise.all(cadastre.unregisteredOwnershipsOwningThisCadastre.map(getUnregisteredOwner));
  } else {
    return BluebirdPromise.resolve([
      {
        cadastre: realty.mapCadastreIdentity(cadastre.ident),
        name: '',
        address: {
          city: '',
          postcode: '',
          title: ''
        },
        cadastreAddress: realty.mapAddress(cadastre.addresses && cadastre.addresses[0]),
        fraction: {
          numerator: 0,
          denominator: 0
        }
      }
    ]);
  }
};

const fetchOwnersData = (cadastre: realty.Cadastre): BluebirdPromise<NeighborCadastreOwner[]> => {
  return getOwnersFromOwnerships(cadastre).then((owners: NeighborCadastreOwner[]) => {
    return owners.map((owner: NeighborCadastreOwner): NeighborCadastreOwner => {
      return {
        cadastre: realty.mapCadastreIdentity(cadastre.ident),
        name: owner.name,
        address: owner.address,
        cadastreAddress: realty.mapAddress(cadastre.addresses[0]),
        fraction: owner.fraction,
        key: owner.key,
        additionalInfo: owner.additionalInfo || '',
        location: owner.location,
        postalAddress: owner.postalAddress,
        coAddress: owner.coAddress,
        role: owner.role
      };
    });
  });
};

/**
 * This function extracts section key (cadastre number for a section) for a given cadastre
 * @param cadastre
 * @returns {BluebirdPromise<string[]>}
 */
const fetchSectionKeys = (cadastre: CoreRealty.ApiCadastre): BluebirdPromise<string[]> => {
  const coreConfig = dataServices.config.getConfig();
  const ident = cadastre.ident;
  const cadastreNumberWithoutSectionPart =
    `${ident.municipalityNumber}-${ident.cadastralUnitNumber}-${ident.unitNumber}-${ident.leaseholdUnitNumber}`;
  const queryParameters = {
    position: false,
    query: cadastreNumberWithoutSectionPart,
    pagesize: 1000
  };
  const link = `${coreConfig.REALTY_ADDRESS}${API_SEARCH_CADASTRES}`;

  return realty.fetchResourcesByLink(link, queryParameters).then((sections) => {
    return sections.filter(section => section.ident.sectionNumber > 0).map(section => section.key);
  });
};

/**
 * This function checks if main cadastre with sections has owners and returns an array with cadastre if it does
 * or if it doesn't have owners it calls function that process the sections and then return an array of cadastre objects
 * based on sections
 * @param cadastre
 * @returns {BluebirdPromise<CoreRealty.ApiCadastre[]>}
 */
const checkOwnersOfCadastreWithSections = (
  cadastre: CoreRealty.ApiCadastre
): BluebirdPromise<CoreRealty.ApiCadastre[]> => {
  if (!cadastre.isSectioned) {
    // square brackets are introduced to have the same return type
    return BluebirdPromise.resolve([cadastre]);
  }

  const queryParameters = {
    pagesize: 1000
  };

  return realty.fetchResourcesByLink(cadastre.links.owners.href, queryParameters).then((owners) => {
    if (owners.length > 0) {
      return BluebirdPromise.resolve([cadastre]);
    }

    return fetchSectionKeys(cadastre).then((sectionKeys: string[]) => {
      return BluebirdPromise.all(sectionKeys.map(realty.fetchCadastres));
    }).then((response: CoreRealty.ApiCadastre[] | CoreRealty.ApiCadastre[][]) => {
      return util.flattenArray(response);
    });
  });
};

const getCadastreOwnersByLink = (cadastreOwnership): BluebirdPromise<any> => {
  return realty.fetchResourcesByLink(cadastreOwnership.links.owningCadastre.href);
};

const getMappedCadastreIdentityFromString = (cadastreKey: string): CoreRealty.MappedCadastreIdentity => {
  const ALL_NUMBERS_REGEX = /(\d+)/g;
  const cadastreNumberSplit = cadastreKey.match(ALL_NUMBERS_REGEX);

  if (!cadastreNumberSplit || cadastreNumberSplit.length < 5) {
    return null;
  }

  return {
    knr: parseInt(cadastreNumberSplit[0], 10),
    gnr: parseInt(cadastreNumberSplit[1], 10),
    bnr: parseInt(cadastreNumberSplit[2], 10),
    fnr: parseInt(cadastreNumberSplit[3], 10),
    snr: parseInt(cadastreNumberSplit[4], 10)
  };
};

const createNeighborDataFromOwnershipInfo = (
  mappedCadastreIdentity: CoreRealty.MappedCadastreIdentity,
  ownershipKeyValueMap: { [key: string]: any; },
  owningCadastresPersons: any
): NeighborData[] => {
  const ownershipKeys: string[] = Object.keys(ownershipKeyValueMap);
  const owners: NeighborData[] = [];

  ownershipKeys.forEach((key: string, ownershipKeyIndex: number) => {
    const ownershipObject = ownershipKeyValueMap[key];
    const associatedOwningCadastres = ownershipObject.associatedOwningCadastres;

    associatedOwningCadastres.forEach((associatedOwningCadastre, aocIndex: number) => {
      associatedOwningCadastre.owningPersons = owningCadastresPersons[ownershipKeyIndex][aocIndex];

      associatedOwningCadastre.owningPersons.forEach((owningPerson) => {
        const neighborData: NeighborData = {
          name: owningPerson.name,
          cadastre: mappedCadastreIdentity,
          address: {
            title: `${owningPerson.location.street} ${owningPerson.location.addressNumber}`,
            postcode: owningPerson.location.postalCode,
            city: owningPerson.location.postalPlace
          },
          fraction: ownershipObject.fraction,
          cadastreAddress: {
            city: '',
            postcode: '',
            title: ''
          },
          additionalInfo: 'Er en del av realsameie og/eller jordsameie'
        };

        owners.push(neighborData);
      });
    });
  });

  return owners;
};

export const fetchAllCadastreOwnersData = (cadastreKey: string): BluebirdPromise<NeighborData[]> => {
  const mappedCadastreIdentity: CoreRealty.MappedCadastreIdentity = getMappedCadastreIdentityFromString(cadastreKey);

  if (!mappedCadastreIdentity) {
    return BluebirdPromise.resolve([]);
  }

  const ownershipKeys: string[] = [];
  const ownershipKeyValueMap: { [key: string]: any; } = {};

  return realty.fetchCadastreOwningCadastre(cadastreKey)
    .then((initialCadastreOwners: ApiOwnership[]) => {
      initialCadastreOwners.forEach((cadastreOwner: ApiOwnership) => {
        ownershipKeyValueMap[cadastreOwner.key] = cadastreOwner;
        ownershipKeys.push(cadastreOwner.key);
      });

      return BluebirdPromise.map(ownershipKeys, (key: string) => getCadastreOwnersByLink(ownershipKeyValueMap[key]));
    })
    .then((owningCadastres: CoreRealty.ApiCadastre[][]) => {

      ownershipKeys.forEach((key: string, i: number) => {
        const associatedOwningCadastres: CoreRealty.ApiCadastre[] = owningCadastres[i];
        ownershipKeyValueMap[key] = { ...ownershipKeyValueMap[key], associatedOwningCadastres };
      });

      return BluebirdPromise.map(ownershipKeys, (key: string) => {
        const associatedOwningCadastres = ownershipKeyValueMap[key].associatedOwningCadastres;

        return BluebirdPromise.map(associatedOwningCadastres, (owningCadastre: CoreRealty.ApiCadastre) => {
          const queryParameters = {
            pagesize: 1000
          };

          return realty.fetchResourcesByLink(owningCadastre.links.owners.href, queryParameters);
        });
      });
    })
    .then((owningCadastresPersons: CoreRealty.ApiPerson[][]) => {
      return createNeighborDataFromOwnershipInfo(mappedCadastreIdentity, ownershipKeyValueMap, owningCadastresPersons);
    })
    .catchReturn([]);
};

export const fetchOwnerDataForCadastreWithOwningCadastres = async (cadastreKeys: string[]): Promise<NeighborData[]> => {
  const alreadyFetchedCadastres: Map<string, realty.Cadastre[]> = new Map();
  const neighborDataList: NeighborData[] = [];

  const apiCadastres = util.flattenArray(await fetchApiCadastresFromCadastreKeys(cadastreKeys));
  let cadastres = [];

  for (const apiCadastre of apiCadastres) {
    const cadastre = await checkOwnersOfCadastreWithSections(apiCadastre);
    cadastres.push(cadastre);
  }

  cadastres = util.flattenArray(cadastres);

  const allCadastreDetails = util.flattenArray(await fetchCadastreDetailsFromApiCadastre(cadastres));

  for (const cadastreDetail of allCadastreDetails) {
    const owningCadastres = await fetchCadastreWithOwnersOwningThisCadastre(cadastreDetail, alreadyFetchedCadastres);
    const ownersOfOwningCadastres = util.flattenArray(
      await Promise.all(
        owningCadastres.map(fetchOwnersData)));

    for (const ownerOfOwningCadastre of ownersOfOwningCadastres) {
      const neighborData = {
        cadastre: realty.mapCadastreIdentity(cadastreDetail.ident),
        name: ownerOfOwningCadastre.name,
        address: ownerOfOwningCadastre.address,
        cadastreAddress: realty.mapAddress(cadastreDetail.addresses[0]),
        fraction: ownerOfOwningCadastre.fraction,
        key: ownerOfOwningCadastre.key,
        additionalInfo: ownerOfOwningCadastre.additionalInfo || '',
        location: ownerOfOwningCadastre.location,
        postalAddress: ownerOfOwningCadastre.postalAddress,
        coAddress: ownerOfOwningCadastre.coAddress,
        role: ownerOfOwningCadastre.role
      };

      if (!doesNeighborDataListHaveDuplicate(neighborDataList, neighborData)) {
        neighborDataList.push(neighborData);
      }
    }
  }

  return neighborDataList.sort((a, b) => a.cadastre.gnr < b.cadastre.gnr ? -1 : 1);
};

const doesNeighborDataListHaveDuplicate = (neighborDataList: NeighborData[], neighborData: NeighborData): boolean => {
  return neighborDataList.some((neighbor: NeighborData) =>    neighbor.name         === neighborData.name         &&
                                                              neighbor.cadastre.gnr === neighborData.cadastre.gnr &&
                                                              neighbor.cadastre.bnr === neighborData.cadastre.bnr &&
                                                              neighbor.cadastre.snr === neighborData.cadastre.snr &&
                                                              neighbor.cadastre.fnr === neighborData.cadastre.fnr &&
                                                              neighbor.cadastre.knr === neighborData.cadastre.knr);
};

const fetchApiCadastresFromCadastreKeys = async (cadastreKeys: string[]): Promise<CoreRealty.ApiCadastre[]> => {
  const resultingApiCadastres: CoreRealty.ApiCadastre[] = util.flattenArray(
    cadastreKeys.map((cadastreKey: string) => realty.fetchCadastres(cadastreKey)));

  return await Promise.all(resultingApiCadastres);
};

const fetchCadastreDetailsFromApiCadastre = async (cadastres: CoreRealty.ApiCadastre[]): Promise<realty.Cadastre[]> => {
  const resultingCadastreDetails = cadastres.map(realty.fetchCadastreDetails);

  return await Promise.all(resultingCadastreDetails);
};

export const fetchOwnerData = async (neighborCadastreKeys: string[]): BluebirdPromise<NeighborData[]> => {
  const neighborCadastreOwners = [];
  let cadastres = [];

  const neighborCadastres = await fetchApiCadastresFromCadastreKeys(neighborCadastreKeys);

  for (const neighborCadastre of neighborCadastres) {
    const cadastre = await checkOwnersOfCadastreWithSections(neighborCadastre);
    cadastres.push(cadastre);
  }

  cadastres = util.flattenArray(cadastres);

  let allCadastreDetails = await fetchCadastreDetailsFromApiCadastre(cadastres);

  allCadastreDetails = allCadastreDetails
    .filter((cadastre: realty.Cadastre) => (
      doesCadastreHaveOwners(cadastre) || doesCadastreHaveUnregisteredOwnershipsOwningThisCadastre(cadastre)
    ))
    .filter((cadastre: realty.Cadastre) => !isHistoricalCadastre(cadastre));

  for (const cadastreDetails of allCadastreDetails) {
    const neighborCadastreOwner = await fetchOwnersData(cadastreDetails);
    neighborCadastreOwners.push(neighborCadastreOwner);
  }

  return util.flattenArray(neighborCadastreOwners);
};

const fetchCadastreWithOwnersOwningThisCadastre = async (cadastre: realty.Cadastre,
                                                         alreadyFetchedCadastres: Map<string, realty.Cadastre[]>
                                                           = new Map()): Promise<realty.Cadastre[]> => {
  if (doesCadastreHaveOwners(cadastre)) {
    return [cadastre];
  }

  if (!doesCadastreHaveOwnershipsOwningThisCadastre(cadastre)) {
    return [cadastre];
  }

  let cadastres: realty.Cadastre[] = [];

  for await (const ownership of cadastre.ownershipsOwningThisCadastre) {
    // CadastreLevel 1 is hjemmelshaver
    if (ownership.owningCadastreIdent && ownership.cadastreLevel.code === '1') {
      const cadastreNumber: string = makeCadastreKeyFromCadastreIdent(ownership.owningCadastreIdent);
      let owningCadastresDetails: realty.Cadastre[];

      if (!alreadyFetchedCadastres.has(cadastreNumber)) {
        const owningCadastres = await realty.fetchCadastres(cadastreNumber);

        owningCadastresDetails = await BluebirdPromise.all(
          owningCadastres.map(realty.fetchCadastreDetails));

        alreadyFetchedCadastres.set(cadastreNumber, owningCadastresDetails);
      } else {
        owningCadastresDetails = alreadyFetchedCadastres.get(cadastreNumber);
      }

      cadastres = [...cadastres, ...owningCadastresDetails];
    }
  }

  return cadastres;
};

export const fetchMainCadastreData = (cadastreNumber: string): BluebirdPromise<realty.Cadastre> => {
  return realty.fetchCadastres(cadastreNumber).then((queryResponse: CoreRealty.ApiCadastre[]) => {
    const cadastre = queryResponse[0];

    return realty.fetchCadastreDetails(cadastre).then((cadastreDetails: realty.Cadastre) => {
      return getOwnersFromOwnerships(cadastreDetails).then((owners: MainCadastreOwner[]) => {
        cadastreDetails.owners = owners;

        return cadastreDetails;
      });
    });
  });
};

const makeCadastreKeyFromCadastreIdent = (cadastreIdent: CoreRealty.CadastreIdent): string => {
  const { municipalityNumber, cadastralUnitNumber, unitNumber, leaseholdUnitNumber, sectionNumber } = cadastreIdent;
  return `${municipalityNumber}-${cadastralUnitNumber}-${unitNumber}-${leaseholdUnitNumber}-${sectionNumber}`;
};

const fetchCounty = (cadastreNumber: string): BluebirdPromise<CodeRegister.County> => {
  const countyId = cadastreNumber.substring(0, COUNTY_ID_LENGTH);

  return dataServices.codeRegister.getCountyById(countyId)
    .then((apiResponse: any) => {
      return apiResponse.counties[0];
    })
    .catch(() => {
      return BluebirdPromise.reject(Error('County info could not be retrieved'));
    });
};

const fetchMunicipality = (cadastreNumber: string): BluebirdPromise<CodeRegister.Municipality> => {
  const municipalityId = cadastreNumber.substring(0, MUNICIPALITY_ID_LENGTH);

  return dataServices.codeRegister.getMunicipalityById(municipalityId)
    .then((apiResponse: any) => {
      return apiResponse.municipalities[0];
    }).catch(() => {
      return BluebirdPromise.reject(Error('Municipality info could not be retrieved'));
    });
};

export const isNeighborListProduct = (productCode: string): boolean => {
  return productCode === PRODUCT_NEIGHBOR_LIST
    || productCode === PRODUCT_NEIGHBOR_LIST_NEW
    || productCode === PRODUCT_NEIGHBOR_LIST_PROD;
};

export const isNeighborListWithCadastreInput = (productCode: string): boolean => {
  return productCode === PRODUCT_NEIGHBOR_LIST_CADASTRE_INPUT;
};

const fetchAndComposeReportData = async (orderData: CoreOrderData.Data): BluebirdPromise<NeighborListReport> => {
  const reportData: NeighborListReport = {
    orderData
  };

  const mainCadastreData = await fetchMainCadastreData(orderData.valgt_eiendom);
  const neighborsData = await fetchOwnerDataForCadastreWithOwningCadastres(orderData.naboeiendom);
  const county = await fetchCounty(orderData.valgt_eiendom);
  const municipality = await fetchMunicipality(orderData.valgt_eiendom);
  const cadastreOwningCadastreNeighborsData = await fetchAllCadastreOwnersData(orderData.valgt_eiendom);

  const orderDataAdditional = orderData.additional;
  const mainCadastreAddressId = orderDataAdditional && orderDataAdditional.mainCadastreAddressId;
  const selectedAddress = realty.findSelectedAddress(mainCadastreAddressId, mainCadastreData.addresses);
  const mainCadastreAddress = selectedAddress || mainCadastreData.addresses[0];

  reportData.cadastre = realty.mapCadastreIdentity(mainCadastreData.ident);
  reportData.owner = mainCadastreData.owners[0];
  reportData.address = realty.mapAddress(mainCadastreAddress);
  reportData.neighbors = neighborsData.concat(cadastreOwningCadastreNeighborsData);
  reportData.county = county;
  reportData.municipality = municipality;

  return reportData;
};

const fetchAndExtractOrderData = (orderId: string, productCode: string): BluebirdPromise<CoreOrderData.Data> => {
  return fetchOrder(orderId).then((order: Order) => {
    const orderData: CoreOrderData.Data = getProductOrderData(productCode, order);

    if (!orderData) {
      return BluebirdPromise.reject(Error(`Order ${orderId} has no product ${productCode}`));
    }

    return BluebirdPromise.resolve(orderData);
  });
};

export const fetchReportDataForCadastreInput = (
  orderId: string,
  productCode: string
): BluebirdPromise<NeighborListReport> => {
  return fetchAndExtractOrderData(orderId, productCode)
    .then(composeOrderDataForCadastreInput)
    .then(fetchAndComposeReportData);
};

export const fetchReportData = (orderId: string, productCode: string): BluebirdPromise<NeighborListReport> => {
  return fetchAndExtractOrderData(orderId, productCode).then(fetchAndComposeReportData);
};

export const generateCSV = (reportData: NeighborListReport): string => {
  const csvRows = [];
  const csvColumnHeaders = [
    'Kommunenummer', 'Gårdsnummer', 'Bruksnummer', 'Festenummer', 'Seksjonsnummer',
    'Eiendommens adresse', 'Eiendommens postnummer', 'Eiendommens poststed',
    'Eier/fester', 'Eiers adresse', 'Eiers postnummer', 'Eiers poststed',
    'Rolle', 'AndelTeller', 'AndelNevner', 'Annet'
  ];
  const mainCadastre = [
    reportData.cadastre.knr, reportData.cadastre.gnr, reportData.cadastre.bnr,
    reportData.cadastre.fnr, reportData.cadastre.snr,
    reportData.address.title, reportData.address.postcode, reportData.address.city,
    reportData.owner.name, reportData.owner.address.title, reportData.owner.address.postcode,
    reportData.owner.address.city, 'Valgt eiendom', reportData.owner.fraction.numerator,
    reportData.owner.fraction.denominator, ''
  ];

  csvRows.push(csvColumnHeaders, mainCadastre);

  reportData.neighbors.forEach((neighbor: NeighborData) => {
    csvRows.push([
      neighbor.cadastre.knr, neighbor.cadastre.gnr, neighbor.cadastre.bnr,
      neighbor.cadastre.fnr, neighbor.cadastre.snr,
      neighbor.cadastreAddress.title, neighbor.cadastreAddress.postcode, neighbor.cadastreAddress.city,
      neighbor.name, neighbor.address.title, neighbor.address.postcode, neighbor.address.city,
      'Tilgrenset eiendom', neighbor.fraction.numerator, neighbor.fraction.denominator, neighbor.additionalInfo
    ]);
  });

  return csvRows.map((csvRow) => {
    return `"${csvRow.join('";"')}"`;
  }).join('\r\n');
};
